<template>
  <div class="fixed z-10 bg-smoke-300 inset-0 w-full h-full" @click="closeModal">
    <div class="lg:p-12 h-full min-h-full overflow-auto">
      <div
        class="lg:rounded lg:shadow-lg max-w-6xl mx-auto bg-gray-200 text-sm overflow-hidden"
        @click.stop
      >
        <div class="bg-white px-6 py-4 flex items-center justify-between">
          <h3 class="font-semibold text-base text-gray-800">
            {{ orderItem ? orderItem.name : 'Loading...' }}
          </h3>
          <button
            :disabled="loading"
            class="inline-flex items-center px-4 py-2 leading-6 rounded-sm border border-gray-200 hover:bg-gray-200 focus:outline-none"
            @click="closeModal"
          >
            <svg
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              stroke="currentColor"
              viewBox="0 0 24 24"
              class="w-5 h-5 -ml-1 mr-2 text-gray-600"
            >
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span>Close</span>
          </button>
        </div>

        <div v-if="orderItem" class="p-6 overflow-auto">
          <ul class="grid gap-8">
            <li>
              <div class="border-2 rounded overflow-hidden">
                <div class="bg-white border-blue-600 px-6 py-4">
                  <h3 class="font-semibold text-base text-gray-800">Links</h3>
                </div>

                <OrderItemLinkCreate
                  v-if="isTypeLinks && isActionCreate"
                  :order-item-id="orderItem.uuid"
                  @creating="setLoading"
                  @created="linkCreated"
                  @cancel="closeForm"
                />

                <OrderItemLinkEdit
                  v-else-if="isTypeLinks && isActionEdit"
                  :link="model"
                  @updating="setLoading"
                  @updated="closeForm"
                  @cancel="closeForm"
                />

                <template v-else>
                  <ul class="bg-white border-t border-gray-200">
                    <li v-if="links.length === 0">
                      <div class="px-6 py-4">
                        <p class="text-teal-600 italic">No Links</p>
                      </div>
                    </li>
                    <li
                      v-else
                      v-for="link in links"
                      :key="link.id"
                      class="border-t border-gray-200 first:border-t-0"
                    >
                      <div class="px-6 py-4">
                        <div>
                          <a
                            :href="link.url"
                            target="_blank"
                            class="text-blue-600 hover:underline"
                            >{{ link.url }}</a
                          >
                          <span v-if="link.title" class="text-gray-600 block">{{
                            link.title
                          }}</span>
                          <p v-if="link.description" class="mt-2">
                            {{ link.description }}
                          </p>
                        </div>

                        <OrderItemLinkDelete
                          v-if="isTypeLinks && isActionDelete && model.id === link.id"
                          :link="model"
                          @destroying="setLoading"
                          @destroyed="linkDestroyed"
                          @cancel="closeForm"
                        />

                        <div v-else class="mt-4 grid grid-flow-col justify-start gap-2">
                          <BaseButton small primary @click="editLink(link)"> Edit </BaseButton>
                          <BaseButton small ghost @click="destroyLink(link)"> Delete </BaseButton>
                        </div>
                      </div>
                    </li>
                  </ul>

                  <div class="bg-gray-100 px-6 py-4 border-t border-gray-200">
                    <button
                      class="bg-blue-600 text-white px-4 py-2 rounded-sm leading-6 text-sm hover:bg-blue-500 focus:outline-none"
                      @click="createLink"
                    >
                      Add Link
                    </button>
                  </div>
                </template>
              </div>
            </li>

            <li>
              <div class="border-2 rounded overflow-hidden">
                <div class="bg-white border-blue-600 px-6 py-4">
                  <h3 class="font-semibold text-base text-gray-800">Notes</h3>
                </div>

                <OrderItemNoteCreate
                  v-if="isTypeNotes && isActionCreate"
                  :order-item-id="orderItem.uuid"
                  @creating="setLoading"
                  @created="noteCreated"
                  @cancel="closeForm"
                />

                <OrderItemNoteEdit
                  v-else-if="isTypeNotes && isActionEdit"
                  :note="model"
                  @updating="setLoading"
                  @updated="closeForm"
                  @cancel="closeForm"
                />

                <template v-else>
                  <ul class="bg-white border-t border-gray-200">
                    <li v-if="notes.length === 0">
                      <div class="px-6 py-4">
                        <p class="text-teal-600 italic">No Notes</p>
                      </div>
                    </li>
                    <li
                      v-else
                      v-for="note in notes"
                      :key="note.id"
                      class="border-t border-gray-200 first:border-t-0"
                    >
                      <div class="px-6 py-4">
                        <div>
                          <p>
                            {{ note.content }}
                          </p>
                        </div>

                        <OrderItemNoteDelete
                          v-if="isTypeNotes && isActionDelete && model.id === note.id"
                          :note="model"
                          @destroying="setLoading"
                          @destroyed="noteDestroyed"
                          @cancel="closeForm"
                        />

                        <div v-else class="mt-4 grid grid-flow-col justify-start gap-2">
                          <BaseButton small primary @click="editNote(note)"> Edit </BaseButton>
                          <BaseButton small ghost @click="destroyNote(note)"> Delete </BaseButton>
                        </div>
                      </div>
                    </li>
                  </ul>

                  <div class="bg-gray-100 px-6 py-4 border-t border-gray-200">
                    <button
                      class="bg-blue-600 text-white px-4 py-2 rounded-sm leading-6 text-sm hover:bg-blue-500 focus:outline-none"
                      @click="createNote"
                    >
                      Add Note
                    </button>
                  </div>
                </template>
              </div>
            </li>

            <li>
              <div class="border-2 rounded overflow-hidden">
                <div class="bg-white border-blue-600 px-6 py-4">
                  <h3 class="font-semibold text-base text-gray-800">Documents</h3>
                </div>

                <OrderItemMediaUpload
                  v-if="isTypeMedia && isActionCreate"
                  :order-item-id="orderItem.uuid"
                  @creating="setLoading"
                  @created="mediumCreated"
                  @cancel="closeForm"
                />

                <!-- <OrderItemNoteEdit
                  v-else-if="isTypeNotes && isActionEdit"
                  :note="model"
                  @updating="setLoading"
                  @updated="closeForm"
                  @cancel="closeForm"
                /> -->

                <template v-else>
                  <ul class="bg-white border-t border-gray-200">
                    <li v-if="media.length === 0">
                      <div class="px-6 py-4">
                        <p class="text-teal-600 italic">No Media</p>
                      </div>
                    </li>
                    <li
                      v-else
                      v-for="medium in media"
                      :key="medium.id"
                      class="border-t border-gray-200 first:border-t-0"
                    >
                      <div class="px-6 py-4">
                        <div>
                          <a :href="medium.url" target="_blank">
                            {{ medium.name }}
                          </a>
                        </div>

                        <OrderItemMediaDelete
                          v-if="isTypeMedia && isActionDelete && model.id === medium.id"
                          :media="medium"
                          @destroying="setLoading"
                          @destroyed="mediumDestroyed"
                          @cancel="closeForm"
                        />

                        <div v-else class="mt-4 grid grid-flow-col justify-start gap-2">
                          <BaseButton small ghost @click="destroyMedium(medium)">
                            Delete
                          </BaseButton>
                        </div>
                      </div>
                    </li>
                  </ul>

                  <div class="bg-gray-100 px-6 py-4 border-t border-gray-200">
                    <button
                      class="bg-blue-600 text-white px-4 py-2 rounded-sm leading-6 text-sm hover:bg-blue-500 focus:outline-none"
                      @click="createMedia"
                    >
                      Add Document
                    </button>
                  </div>
                </template>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OrderItemLinkCreateFormVue from './OrderItemLinkCreateForm.vue';
import OrderItemLinkEditFormVue from './OrderItemLinkEditForm.vue';
import OrderItemLinkDeleteFormVue from './OrderItemLinkDeleteForm.vue';
import OrderItemNoteCreateFormVue from './OrderItemNoteCreateForm.vue';
import OrderItemNoteEditFormVue from './OrderItemNoteEditForm.vue';
import OrderItemNoteDeleteFormVue from './OrderItemNoteDeleteForm.vue';
import OrderItemMediaUploadFormVue from './OrderItemMediaUploadForm.vue';
import OrderItemMediaDeleteFormVue from './OrderItemMediaDeleteForm.vue';

const ACTION_CREATE = 'CREATE';
const ACTION_EDIT = 'EDIT';
const ACTION_DELETE = 'DELETE';

const TYPE_LINKS = 'links';
const TYPE_NOTES = 'notes';
const TYPE_MEDIA = 'media';

export default {
  components: {
    OrderItemLinkCreate: OrderItemLinkCreateFormVue,
    OrderItemLinkEdit: OrderItemLinkEditFormVue,
    OrderItemLinkDelete: OrderItemLinkDeleteFormVue,
    OrderItemNoteCreate: OrderItemNoteCreateFormVue,
    OrderItemNoteEdit: OrderItemNoteEditFormVue,
    OrderItemNoteDelete: OrderItemNoteDeleteFormVue,
    OrderItemMediaUpload: OrderItemMediaUploadFormVue,
    OrderItemMediaDelete: OrderItemMediaDeleteFormVue,
  },

  props: {
    orderItemUuid: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      action: null,
      type: null,
      model: null,
      createdLinks: [],
      createdNotes: [],
      createdMedia: [],
    };
  },

  computed: {
    orderItem() {
      return this.$store.getters['ui/orderItems/orderItem'];
    },
    links() {
      const { links = [] } = this.orderItem || {};
      return [...this.createdLinks, ...links]
        .map((id) => this.$store.state.entities.links.byId[id] || false)
        .filter((item) => item);
    },
    notes() {
      const { notes = [] } = this.orderItem || {};
      return [...this.createdNotes, ...notes]
        .map((id) => this.$store.state.entities.notes.byId[id] || false)
        .filter((item) => item);
    },
    media() {
      const { media = [] } = this.orderItem || {};
      return [...this.createdMedia, ...media]
        .map((id) => this.$store.state.entities.media.byId[id] || false)
        .filter((item) => item);
    },
    isActionCreate() {
      return this.action === ACTION_CREATE;
    },
    isActionEdit() {
      return this.action === ACTION_EDIT;
    },
    isActionDelete() {
      return this.action === ACTION_DELETE;
    },
    isTypeLinks() {
      return this.type === TYPE_LINKS;
    },
    isTypeNotes() {
      return this.type === TYPE_NOTES;
    },
    isTypeMedia() {
      return this.type === TYPE_MEDIA;
    },
  },

  created() {
    this.init();
  },

  destroyed() {
    this.$store.commit({
      type: 'ui/orderItems/setCurrent',
      current: null,
    });
  },

  methods: {
    async init() {
      this.setLoading(true);

      try {
        await this.$store.dispatch({
          type: 'ui/orderItems/read',
          orderItemUuid: this.orderItemUuid,
        });
      } catch (error) {
        this.$emit('close');
      } finally {
        this.setLoading(false);
      }
    },
    setLoading(value) {
      this.loading = value;
    },
    create(type) {
      if (!type) return;
      if ([TYPE_LINKS, TYPE_NOTES, TYPE_MEDIA].indexOf(type) === -1) return;
      this.action = ACTION_CREATE;
      this.type = type;
    },

    edit(type, model = null) {
      if (!type) return;
      if ([TYPE_LINKS, TYPE_NOTES, TYPE_MEDIA].indexOf(type) === -1) return;
      this.action = ACTION_EDIT;
      this.type = type;
      this.model = model;
    },

    delete(type, model = null) {
      if (!type) return;
      if ([TYPE_LINKS, TYPE_NOTES, TYPE_MEDIA].indexOf(type) === -1) return;
      this.action = ACTION_DELETE;
      this.type = type;
      this.model = model;
    },

    closeForm() {
      this.action = null;
      this.type = null;
      this.model = null;
    },

    createLink() {
      this.create(TYPE_LINKS);
    },

    linkCreated({ id }) {
      this.createdLinks.push(id);
      this.closeForm();
    },

    linkDestroyed() {
      this.closeForm();
      this.setLoading(false);
    },

    editLink(link) {
      this.edit(TYPE_LINKS, link);
    },

    destroyLink(link) {
      this.delete(TYPE_LINKS, link);
    },

    createNote() {
      this.create(TYPE_NOTES);
    },

    noteCreated({ id }) {
      this.createdNotes.push(id);
      this.closeForm();
    },

    editNote(note) {
      this.edit(TYPE_NOTES, note);
    },

    destroyNote(note) {
      this.delete(TYPE_NOTES, note);
    },

    noteDestroyed() {
      this.closeForm();
      this.setLoading(false);
    },

    createMedia() {
      this.create(TYPE_MEDIA);
    },

    mediumCreated({ id }) {
      this.createdMedia.push(id);
      this.closeForm();
    },

    destroyMedium(medium) {
      this.delete(TYPE_MEDIA, medium);
    },

    mediumDestroyed() {
      this.closeForm();
      this.setLoading(false);
    },

    closeModal() {
      if (this.loading) return;

      this.$emit('close');
    },
  },
};
</script>
